import { useApi } from '../api/context';
import { QrCode } from '../components/QrCode';
import { ErrorNotice, Loading, PageHeader, ZinqLogo } from '../components/ui';
import { useI18n } from '../i18n';
import { formatPrice } from '../lib/money';
import { useLoad } from '../lib/useLoad';
import { useMerchant } from '../state/merchant';

/** QR_MIN_AMOUNT_MINOR default, used if the API does not say (50 ден.). */
const DEFAULT_MIN_AMOUNT_MINOR = 5_000;

/**
 * The static counter QR as a printable A6 card: the customer scans it, enters the amount and
 * approves in their own bank. The QR is drawn here from the EMVCo payload; the merchant code and
 * the payment code are printed under it so a customer can compare them with what the app shows.
 */
export function CounterQrPage() {
  const { t } = useI18n();
  const api = useApi();
  const merchant = useMerchant();
  const { state, reload } = useLoad((_signal) => api.staticQr(), [api, merchant.id]);

  if (state.status === 'loading') return <Loading />;
  if (state.status === 'error') {
    return (
      <>
        <PageHeader title={t('qr.title')} />
        <ErrorNotice error={state.error} onRetry={() => reload()} />
      </>
    );
  }
  const { payload, intent } = state.data;
  const min = formatPrice(state.data.minAmountMinor ?? DEFAULT_MIN_AMOUNT_MINOR);

  return (
    <>
      <PageHeader
        title={t('qr.title')}
        subtitle={t('qr.subtitle')}
        actions={
          <button type="button" className="btn btn--primary no-print" onClick={() => window.print()}>
            {t('qr.print')}
          </button>
        }
      />
      {!merchant.receiveAccount.active ? (
        <div className="notice notice--warning no-print" role="alert">
          <p>{t('qr.inactive')}</p>
        </div>
      ) : null}
      <div className="qr-stage">
        <article className="qr-card" data-testid="qr-card" aria-label={t('qr.title')}>
          <header className="qr-card-head">
            <ZinqLogo size={30} />
            <span className="qr-card-brand">ZINQ</span>
          </header>
          <h2 className="qr-card-name">{merchant.displayName}</h2>
          <p className="qr-card-city">{merchant.city}</p>
          <div className="qr-card-code">
            <QrCode payload={payload} label={t('qr.alt', { name: merchant.displayName })} size={232} />
          </div>
          <p className="qr-card-cta">{t('qr.cta')}</p>
          <dl className="qr-card-ids">
            <div>
              <dt>{t('qr.merchantCode')}</dt>
              <dd className="mono" data-testid="qr-merchant-code">
                {merchant.code}
              </dd>
            </div>
            <div>
              <dt>{t('qr.reference')}</dt>
              <dd className="mono" data-testid="qr-reference">
                {intent.reference}
              </dd>
            </div>
          </dl>
          <p className="qr-card-min">{t('qr.min', { amount: min })}</p>
          <p className="qr-card-note">{t('qr.noCamera')}</p>
        </article>
        <aside className="qr-aside no-print">
          <p>{t('qr.never')}</p>
          <p>{t('qr.noFee')}</p>
        </aside>
      </div>
    </>
  );
}
